import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { downloadBlob, formatApiError } from "@/lib/api";
import { Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Download, FileText, ArrowRight, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { formatINR, monthName } from "@/utils/format";
import { useNavigate } from "react-router-dom";

export default function EmployeeDashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/portal/dashboard").then(({ data }) => setD(data)).catch(() => setD({})); }, []);
  if (d === null) return <Loading />;

  const latest = d.latest_payslip;
  const download = () => latest && downloadBlob(`/portal/payslips/${latest.id}/download`,
    `Payslip_${d.employee_id}_${monthName(latest.month || 0)}_${latest.year}.pdf`).catch((e) => toast.error(formatApiError(e)));

  return (
    <div data-testid="employee-dashboard">
      <div className="mb-6">
        <div className="label-caps">Welcome back</div>
        <h1 className="font-display text-2xl font-semibold mt-1">{d.full_name}</h1>
        <p className="text-sm text-muted-foreground">{d.designation} · {d.employee_id}</p>
      </div>

      {/* Net pay hero card */}
      <div className="rounded-lg bg-[#0A0A0A] text-white p-6 relative overflow-hidden" data-testid="netpay-card">
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#FF4500]" />
        <div className="label-caps text-white/50">{latest ? `${latest.month_name} ${latest.year} · Net pay` : "Net pay"}</div>
        {latest ? (
          <>
            <div className="font-display text-4xl font-semibold mt-2 tabular">{formatINR(latest.net)}</div>
            <div className="text-xs text-white/40 mt-1">{latest.reference}</div>
            <Button onClick={download} className="mt-5 bg-[#FF4500] hover:bg-[#e63e00] gap-1.5" data-testid="download-latest-button">
              <Download className="w-4 h-4" /> Download payslip
            </Button>
          </>
        ) : (
          <div className="text-white/60 mt-3 text-sm">No payslips released yet. Your first payslip will appear here once payroll is run.</div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Link to="/portal/payslips" className="bg-card border border-border rounded-md p-5 flex items-center justify-between hover:-translate-y-[1px] transition-transform group" data-testid="goto-payslips">
          <div>
            <div className="flex items-center gap-2 font-medium"><FileText className="w-4 h-4 text-[#FF4500]" /> All payslips</div>
            <div className="text-xs text-muted-foreground mt-1">{d.payslip_count || 0} available</div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#FF4500]" />
        </Link>
        <button onClick={() => navigate("/change-password")} className="bg-card border border-border rounded-md p-5 flex items-center justify-between hover:-translate-y-[1px] transition-transform text-left group" data-testid="goto-change-password">
          <div>
            <div className="flex items-center gap-2 font-medium"><KeyRound className="w-4 h-4 text-[#FF4500]" /> Change password</div>
            <div className="text-xs text-muted-foreground mt-1">Keep your account secure</div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[#FF4500]" />
        </button>
      </div>
    </div>
  );
}
